import React from 'react';
import { Link } from 'react-router-dom';
import { treks, testimonials, foodMenu } from '../data/trekData';
import { ArrowRight, Mountain, Shield, Users, Award, MapPin, Clock, TrendingUp, Soup, Utensils, Coffee, UtensilsCrossed } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import Avatar from '@/components/ui/avatar';
import PriceAnimation from '@/components/ui/price-animation';


const Home = () => {
  const featuredTreks = treks.slice(0, 3);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background with overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/50 to-slate-900 z-10" />
          <img
            src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80"
            alt="Mountain landscape"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="relative z-20 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-in fade-in slide-in-from-bottom-10 duration-700">
            Conquer the <span className="text-orange-500">Mountains</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-10 duration-700 delay-150">
            Experience breathtaking mountain treks across India with expert guides and unforgettable adventures
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-in fade-in slide-in-from-bottom-10 duration-700 delay-300">
            <Button
              asChild
              size="lg"
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg group"
            >
              <Link to="/treks">
                Explore Treks
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>

        {/* Scroll indicator */}
        {/* <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white rounded-full" />
          </div>
        </div> */}
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Choose Us</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're committed to providing safe, memorable, and transformative trekking experiences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-orange-600" />
                </div>
                <CardTitle>Safety First</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Certified guides with first aid training and comprehensive safety equipment
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-orange-600" />
                </div>
                <CardTitle>Expert Guides</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Experienced trek leaders with deep knowledge of Himalayan terrain and culture
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Mountain className="h-6 w-6 text-orange-600" />
                </div>
                <CardTitle>Best Routes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Carefully curated trails showcasing the most stunning Himalayan landscapes
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Award className="h-6 w-6 text-orange-600" />
                </div>
                <CardTitle>Quality Service</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Premium camping equipment and excellent support throughout your journey
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Treks Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Featured Treks</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover our most popular trekking adventures across the Indian Himalayas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredTreks.map((trek) => (
              <Card
                key={trek.id}
                className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={trek.image}
                    alt={trek.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-orange-600 hover:bg-orange-700 text-white">
                      {trek.difficulty}
                    </Badge>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-2xl group-hover:text-orange-600 transition-colors">
                    {trek.name}
                  </CardTitle>
                  <CardDescription className="flex items-center text-gray-600">
                    <MapPin className="h-4 w-4 mr-1" />
                    {trek.location}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{trek.shortDescription}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {trek.duration}
                    </div>
                    <div className="flex items-center">
                      <TrendingUp className="h-4 w-4 mr-1" />
                      {trek.altitude}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-500 pb-1.5">Starting from</div>
                    <div className="max-w-[150px] overflow-hidden text-ellipsis">
                      <PriceAnimation actual={trek.actualPrice} offer={trek.offerPrice} />
                    </div>
                    <div className="text-sm text-gray-500 pt-1.5">per person</div>
                  </div>
                  <Button
                    asChild
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    <Link to={`/trek/${trek.id}`}>
                      View Details
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white"
            >
              <Link to="/treks">View All Treks</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Food Menu Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Food Menu</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We’re committed to fueling your adventures with flavorful, wholesome meals that make every journey unforgettable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="flex flex-row items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Soup className="h-6 w-6 text-orange-600" />
                </div>

                <CardTitle>Breakfast</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {foodMenu.breakfast.map((item, index) => (
                    <li key={index} className="flex items-start text-gray-700">
                      <span className="text-xl mr-2 mt-1 leading-none">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <span className="font-bold">Note: Each morning, we serve one or two freshly prepared breakfast item with a hot cup of tea.</span>
              </CardFooter>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="flex flex-row items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Utensils className="h-6 w-6 text-orange-600" />
                </div>
                <CardTitle>Lunch</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-disc list-inside space-y-2">
                  {foodMenu.lunch.map((item, index) => (
                    <li key={index} className="flex items-start text-gray-700">
                      <span className="text-xl mr-2 mt-1 leading-none">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <span className="font-bold">Note: Any one lunch item will be packed and provided for the trek each day.</span>
              </CardFooter>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="flex flex-row items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Coffee className="h-6 w-6 text-orange-600" />
                </div>
                <CardTitle>Evening Snacks</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {foodMenu.snacks.map((item, index) => (
                    <li key={index} className="flex items-start text-gray-700">
                      <span className="text-xl mr-2 mt-1 leading-none">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <span className="font-bold">Note: One evening snack item along with tea will be provided after the trek.</span>
              </CardFooter>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="flex flex-row items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <UtensilsCrossed className="h-6 w-6 text-orange-600" />
                </div>
                <CardTitle>Dinner</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {foodMenu.dinner.map((item, index) => (
                    <li key={index} className="flex items-start text-gray-700">
                      <span className="text-xl mr-2 mt-1 leading-none">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <span className="font-bold">Note: Any one hot dinner meal will be served fresh at the campsite each evening.</span>
              </CardFooter>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">What Our Trekkers Say</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real experiences from adventurers who've trekked with us
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-none shadow-lg">
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    {/* <Avatar className="h-12 w-12 bg-orange-600">
                      <AvatarFallback className="text-white font-semibold">
                        {testimonial.avatar}
                      </AvatarFallback>
                    </Avatar> */}
                    <Avatar name={testimonial.name} image={testimonial.avatar}></Avatar>
                    <div>
                      <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                      <CardDescription>{testimonial.trek}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-yellow-500 text-lg">★</span>
                    ))}
                  </div>
                  <p className="text-gray-600 italic">"{testimonial.comment}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready for Your Next Adventure?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join us for an unforgettable journey through the majestic Himalayas
          </p>
          <Button
            asChild
            size="lg"
            className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg"
          >
            <Link to="/contact">Book Your Trek Now</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;